import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Loader2, ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";


export default function Login() {
  useEffect(() => {
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("robots", "noindex, nofollow");
    document.title = "Host Login | Truestays";
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await base44.auth.loginViaEmailPassword(email, password);
      window.location.href = "/";
    } catch (err) {
      setError(err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const valueProps = [
    "Manage your listings and bookings",
    "Track live earnings and occupancy",
    "Message guests and your local team",
  ];

  return (
    <div style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr 1fr" }} className="login-split">
      {/* Brand panel */}
      <div className="login-brand" style={{ background: "#112215", color: "#fff", padding: "64px 56px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Link to="/" style={{ textDecoration: "none", marginBottom: 48, display: "inline-block" }}>
          <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 26, width: "auto" }} />
        </Link>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(26px,3vw,34px)", color: "#fff", margin: "0 0 16px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>Welcome back to your owner dashboard</h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "rgba(255,255,255,0.6)", margin: "0 0 32px", lineHeight: 1.6 }}>Log in to manage your short-let property, track performance and stay in control.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {valueProps.map((v) => (
            <div key={v} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CheckCircle2 size={14} color="#fff" />
              </div>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.85)" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Form panel */}
      <div className="login-form" style={{ background: "#fff", padding: "64px 56px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ maxWidth: 420, margin: "0 auto", width: "100%" }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: "#888", textDecoration: "none", marginBottom: 24, transition: "color 0.2s" }} onMouseEnter={e => e.currentTarget.style.color = "#1A1A1A"} onMouseLeave={e => e.currentTarget.style.color = "#888"}>
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 30, color: "#1A1A1A", margin: "0 0 8px", letterSpacing: "-0.02em" }}>Host Login</h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#888", margin: "0 0 28px" }}>Welcome back. Please enter your details.</p>


          {error && <div style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 10, background: "#E8EDFF", color: "#C4C024", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>{error}</div>}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#888", display: "block", marginBottom: 6 }}>Email</label>
              <input type="email" autoComplete="email" autoFocus placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #E5E5E5", borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }} onFocus={e => e.target.style.borderColor = "#1A1A1A"} onBlur={e => e.target.style.borderColor = "#E5E5E5"} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#888" }}>Password</label>
                <Link to="/forgot-password" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#1A1A1A", textDecoration: "none" }}>Forgot?</Link>
              </div>
              <input type="password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #E5E5E5", borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }} onFocus={e => e.target.style.borderColor = "#1A1A1A"} onBlur={e => e.target.style.borderColor = "#E5E5E5"} />
            </div>
            <button type="submit" disabled={loading} style={{ width: "100%", background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#000"}
              onMouseLeave={e => e.currentTarget.style.background = "#112215"}>
              {loading ? (<><Loader2 size={16} className="animate-spin" /> Logging in…</>) : (<>Log in <ArrowRight size={16} /></>)}
            </button>
          </form>

          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#888", margin: "28px 0 0", textAlign: "center" }}>Contact support to request an account.</p>
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .login-split { grid-template-columns: 1fr !important; }
          .login-brand { display: none !important; }
          .login-form { padding: 80px 24px !important; }
        }
      `}</style>
    </div>
  );
}